// =============================================================================
// Lumen Viewer — PDF report export  (client-side via jsPDF)
// =============================================================================
// Produces a one-page report with the Lumen wordmark, study metadata,
// findings/impression/recs, measurements, and a single key image snapshot.

import jsPDF from 'jspdf';

export function buildReportPdf({ study, aiRead, measurements = [], canvasDataUrl = null, signedByUpn = null }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = 48;

  // Header band
  doc.setFillColor(22, 24, 32);
  doc.rect(0, 0, PAGE_W, 54, 'F');
  doc.setTextColor(45, 212, 160); // teal
  doc.setFont('Times', 'italic');
  doc.setFontSize(22);
  doc.text('Lumen', M, 34);
  doc.setTextColor(200, 200, 210);
  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('by UnityMSK', M + 70, 34);

  doc.setTextColor(180, 180, 190);
  doc.setFontSize(9);
  doc.text(
    aiRead?.finalized || aiRead?.signed_at ? 'SIGNED RADIOLOGY REPORT' : 'AI PRELIMINARY REPORT',
    PAGE_W - M, 34, { align: 'right' }
  );

  // Diagnostic-use disclaimer band right under the header.
  doc.setFillColor(80, 60, 10);                      // amber-tint
  doc.rect(0, 54, PAGE_W, 18, 'F');
  doc.setTextColor(251, 191, 36);
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(
    'FOR VIEWING ONLY — NOT FOR PRIMARY DIAGNOSIS · Image quality not validated for diagnostic interpretation.',
    PAGE_W / 2, 66, { align: 'center' }
  );
  doc.setTextColor(20, 20, 25);

  // Body
  doc.setTextColor(20, 20, 25);
  let y = 100;

  // Study metadata block
  doc.setFont('Helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Study', M, y); y += 14;
  doc.setFont('Helvetica', 'normal'); doc.setFontSize(9);

  const kv = [
    ['Patient',        study?.patient_name || '—'],
    ['MRN',            study?.patient_id || '—'],
    ['DOB',            study?.patient_dob || '—'],
    ['Study date',     study?.study_date || '—'],
    ['Modality',       `${study?.modality || '—'}  ·  ${study?.body_part || ''}`],
    ['Accession',      study?.accession_number || '—'],
    ['Description',    study?.study_description || '—'],
    ['Location',       `${study?.location_name || ''}${study?.location_ae ? '  (' + study.location_ae + ')' : ''}`],
    ['Ordering MD',    study?.ordering_provider || '—'],
    ['Study UID',      study?.study_instance_uid || '—'],
  ];
  for (const [k, v] of kv) {
    doc.setTextColor(120, 120, 130); doc.text(k + ':', M, y);
    doc.setTextColor(20, 20, 25); doc.text(String(v), M + 90, y);
    y += 12;
  }
  y += 8;

  // Key image
  if (canvasDataUrl) {
    const imgW = (PAGE_W - M * 2);
    const imgH = 260;
    doc.setDrawColor(200, 200, 200);
    doc.rect(M, y, imgW, imgH);
    try { doc.addImage(canvasDataUrl, 'JPEG', M, y, imgW, imgH); } catch (_) {}
    y += imgH + 14;
  }

  // Findings
  doc.setFont('Helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Findings', M, y); y += 14;
  doc.setFont('Helvetica', 'normal'); doc.setFontSize(10);
  const findings = Array.isArray(aiRead?.edited_findings)
    ? aiRead.edited_findings
    : (Array.isArray(aiRead?.findings) ? aiRead.findings : safeJsonArray(aiRead?.edited_findings) || safeJsonArray(aiRead?.findings) || []);
  if (findings.length === 0) {
    doc.setTextColor(120, 120, 130); doc.text('(none reported)', M, y); y += 12;
    doc.setTextColor(20, 20, 25);
  } else {
    for (const f of findings) {
      const lines = doc.splitTextToSize('• ' + f, PAGE_W - M * 2 - 12);
      doc.text(lines, M, y);
      y += lines.length * 12 + 2;
      if (y > PAGE_H - 120) { doc.addPage(); y = M; }
    }
  }
  y += 6;

  // Impression
  doc.setFont('Helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Impression', M, y); y += 14;
  doc.setFont('Helvetica', 'italic'); doc.setFontSize(10);
  const imp = aiRead?.edited_impression || aiRead?.impression || '(none)';
  const lines = doc.splitTextToSize(imp, PAGE_W - M * 2);
  doc.text(lines, M, y); y += lines.length * 12 + 4;

  // Measurements
  if (measurements.length) {
    doc.setFont('Helvetica', 'bold'); doc.setFontSize(11);
    doc.text('Measurements', M, y); y += 14;
    doc.setFont('Courier', 'normal'); doc.setFontSize(9);
    for (const m of measurements) {
      const val = m.value_mm != null ? `${m.value_mm.toFixed(1)} mm`
                : m.value_deg != null ? `${m.value_deg.toFixed(1)}°` : '';
      const row = `${m.kind.padEnd(12)} ${val.padStart(10)}  ${m.label || ''}`;
      doc.text(row, M, y); y += 11;
      if (y > PAGE_H - 80) { doc.addPage(); y = M; doc.setFont('Courier', 'normal'); doc.setFontSize(9); }
    }
    y += 6;
  }

  // Signature block
  doc.setDrawColor(200, 200, 200);
  doc.line(M, PAGE_H - 64, PAGE_W - M, PAGE_H - 64);
  doc.setFont('Helvetica', 'normal'); doc.setFontSize(9);
  doc.setTextColor(100, 100, 110);
  const signedBy = aiRead?.signed_by || signedByUpn || '';
  const signedAt = aiRead?.signed_at || '';
  doc.text(
    signedBy ? `Electronically signed by ${signedBy}${signedAt ? '  ·  ' + String(signedAt).slice(0, 19).replace('T', ' ') : ''}`
             : 'AI preliminary — not yet signed',
    M, PAGE_H - 46
  );
  doc.text('Lumen by UnityMSK · HIPAA-compliant PACS · AES-256 encrypted', PAGE_W - M, PAGE_H - 46, { align: 'right' });

  // Footer notice repeated bottom-of-page.
  doc.setTextColor(140, 90, 0);
  doc.setFont('Helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.text(
    'Lumen is not an FDA-cleared diagnostic display. Findings here are for clinical reference only and must not be the sole basis for diagnosis.',
    M, PAGE_H - 32, { maxWidth: PAGE_W - M * 2 }
  );

  return doc;
}

function safeJsonArray(s) {
  if (!s) return null;
  try { const p = JSON.parse(s); return Array.isArray(p) ? p : null; } catch { return null; }
}

export function downloadReportPdf(opts) {
  const doc = buildReportPdf(opts);
  const fname = `lumen-report-${(opts.study?.accession_number || opts.study?.study_instance_uid || 'study').replace(/[^a-z0-9._-]/gi, '_')}.pdf`;
  doc.save(fname);
}
